import { isEmpty } from 'lodash';

import type { DeepRemoveTypename } from './types';

// We check whether data is empty as well because of a bug in Apollo:
// https://github.com/apollographql/react-apollo/issues/1314
export const isLoading = (queryObj: { loading: boolean; data: unknown | undefined }) =>
  queryObj.loading || isEmpty(queryObj.data);

interface MaybeLoading<T, U> {
  loading: boolean;
  data: T | undefined;
  previousData?: T | undefined;
  variables?: U | undefined;
}
interface NotLoading<T, U> {
  loading: false;
  data: T;
  previousData?: T | undefined;
  variables: U;
}
interface HasData<T, U> {
  loading: boolean;
  data: T;
  previousData: undefined;
  variables: U;
}
interface HasPreviousData<T, U> {
  loading: boolean;
  data: undefined;
  previousData: T;
  variables: U;
}

export const isDoneLoading = <T, U>(queryObj: MaybeLoading<T, U>): queryObj is NotLoading<T, U> =>
  !isLoading(queryObj);

/**
 * Use when you want to show a load indicator on initial fetch, but not on
 * subsequent updates.
 *
 * Note: May return stale data while new data is getting fetched.
 */
export const hasData = <T, U>(
  queryObj: MaybeLoading<T, U>,
): queryObj is HasData<T, U> | HasPreviousData<T, U> =>
  !isEmpty(queryObj.data) || !isEmpty(queryObj.previousData);

interface ExponentialDelayOpts {
  /**
   * The default delay for each count, as well as the exact delay
   * before the first attempt is made (`count` = 0).
   */
  initialDelay: number;
  /**
   * The current retry attempt. 0-based.
   */
  count: number;
  /**
   * Can be used to alter the delay specific to the current count.
   * @default 1
   */
  factor?: number;
}

/**
 * @returns A delay in milliseconds that gets exponentially larger
 * the more retry attempts (`count`) are made.
 * See also https://www.apollographql.com/docs/react/api/link/apollo-link-retry/#avoiding-thundering-herd
 */
export const getExponentialDelay = ({ count, initialDelay, factor = 1 }: ExponentialDelayOpts) => {
  const exponentialCount = count ** 2;

  // Delays specific to current count
  const currentDelay = factor * initialDelay;
  const currentExponentialDelay = currentDelay * exponentialCount;

  return initialDelay + currentExponentialDelay;
};

export const combineRefetchQueries = (queryNames: string[]) => Array.from(new Set(queryNames));

export const typeSafeObjectEntries = <T extends object>(object: T) =>
  Object.entries(object) as [keyof T, T[keyof T]][];

export const removeTypename = <T>(obj: T): DeepRemoveTypename<T> => {
  // handle all leaf cases
  if (!obj || typeof obj !== 'object') {
    return obj as DeepRemoveTypename<T>;
  }

  // handle arrays
  if (Array.isArray(obj)) {
    return [...obj].map(removeTypename) as DeepRemoveTypename<T>;
  }

  // otherwise it's an object
  return typeSafeObjectEntries(obj).reduce((acc, [key, value]) => {
    if (key !== '__typename') {
      acc[key] = removeTypename(value) as T[typeof key];
    }
    return acc;
  }, {} as T) as DeepRemoveTypename<T>;
};
